
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Brain, Shield, LineChart, Users, Globe, Database, Bot, Sparkles } from "lucide-react";

const KeyThemesSection = () => {
  const themes = [
    {
      id: 7,
      title: "Data & Infrastructure",
      description: "Best practices for building robust AI infrastructure, data pipelines, and scaling AI systems",
      icon: <Database className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-orange-600 to-red-600"
    },
    {
      id: 8,
      title: "Emerging AI Technologies",
      description: "Exploring reinforcement learning, robotics, computer vision, and other cutting-edge AI fields",
      icon: <Bot className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-red-600 to-purple-600"
    },
    {
      id: 1,
      title: "Generative AI Revolution",
      description: "Explore the latest breakthroughs in generative models, text-to-image technologies, and creative AI applications",
      icon: <Sparkles className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-purple-600 to-blue-600"
    },
    {
      id: 2,
      title: "Responsible AI & Ethics",
      description: "Discussing fairness, accountability, transparency, and ethical considerations in AI development",
      icon: <Shield className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-blue-600 to-cyan-600"
    },
    {
      id: 3,
      title: "Enterprise AI Integration",
      description: "Strategies for successfully implementing AI solutions across business operations and processes",
      icon: <LineChart className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-cyan-600 to-teal-600"
    },
    {
      id: 4,
      title: "AI & Human Collaboration",
      description: "How AI can augment human capabilities rather than replace them across various industries",
      icon: <Users className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-teal-600 to-green-600"
    },
    {
      id: 5,
      title: "Foundation Models",
      description: "Deep dive into large-scale pre-trained models that are reshaping the AI landscape",
      icon: <Brain className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-green-600 to-yellow-600"
    },
    {
      id: 6,
      title: "AI for Global Challenges",
      description: "How artificial intelligence is being deployed to address climate change, healthcare, and other global issues",
      icon: <Globe className="h-12 w-12 text-white" />,
      color: "bg-gradient-to-r from-yellow-600 to-orange-600"
    },
  ];

  return (
    <section id="key-themes" className="py-20 bg-gradient-to-b from-blue-100 via-blue-50 to-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-blue-600 font-medium mb-3">Key Themes</h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 mb-6">
            Exploring the Future of AI
          </h3>
          <p className="max-w-3xl mx-auto text-lg text-blue-800">
            Our conference covers a wide range of essential topics that are shaping the future of artificial intelligence.
            From cutting-edge research to practical applications, these themes reflect the most important developments in AI today.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {themes.map((theme, index) => (
            <Card 
              key={theme.id} 
              className={`overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 opacity-0 ${
                index % 2 === 0 ? 'animate-slide-in-left' : 'animate-slide-in-right'
              }`}
              style={{ 
                animationDelay: `${index * 150}ms`,
                animationFillMode: 'forwards'
              }}
            >
              <div className={`${theme.color} p-6 flex justify-center hover:scale-105 transition-transform duration-300`}>
                {theme.icon}
              </div>
              <CardContent className="p-6 bg-white hover:bg-blue-50 transition-colors duration-300">
                <h4 className="text-xl font-bold text-blue-900 mb-3">{theme.title}</h4>
                <p className="text-blue-700">{theme.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyThemesSection;
